import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createControllerSource,
  deleteControllerSource,
  getEnergySummary,
  getLatestProbeValues,
  getProbeHistory,
  SENSOR_PARAMETER_IDS,
  listControllerSources,
  listSensorMappings,
  listSensorReadings,
  revokeControllerSource,
  setEnergyPrice,
  updateSensorMapping,
  type UpdateSensorMappingInput,
} from '../api/controller';

/** Query keys of the controller data (#1899); everything of one aquarium shares the prefix. */
export const controllerKeys = {
  all: (aquariumId: string | undefined) => ['controller', aquariumId] as const,
  sources: (aquariumId: string | undefined) => ['controller', aquariumId, 'sources'] as const,
  mappings: (aquariumId: string | undefined) => ['controller', aquariumId, 'mappings'] as const,
  readings: (aquariumId: string | undefined, mappingId: string, days: number) =>
    ['controller', aquariumId, 'readings', mappingId, days] as const,
  energy: (aquariumId: string | undefined, year: number) => ['controller', aquariumId, 'energy', year] as const,
  probeValues: (aquariumId: string | undefined) => ['controller', aquariumId, 'probe-values'] as const,
  probeHistory: (aquariumId: string | undefined, range: string, ids: readonly string[]) =>
    ['controller', aquariumId, 'probe-history', range, ids.join(',')] as const,
};

/** Latest tank probe value per parameter — shown next to the test values. */
export function useLatestProbeValues(aquariumId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: controllerKeys.probeValues(aquariumId),
    queryFn: () => getLatestProbeValues(requireAquarium(aquariumId)),
    enabled: enabled && Boolean(aquariumId),
  });
}

/** Probe line behind the measurement charts; only parameters a probe can measure are asked for. */
export function useProbeHistory(aquariumId: string | undefined, range: string, parameterIds: readonly string[]) {
  const ids = parameterIds.filter((id) => (SENSOR_PARAMETER_IDS as readonly string[]).includes(id)).slice(0, 3);
  return useQuery({
    queryKey: controllerKeys.probeHistory(aquariumId, range, ids),
    queryFn: () => getProbeHistory(requireAquarium(aquariumId), range, ids),
    enabled: Boolean(aquariumId) && ids.length > 0,
  });
}

function requireAquarium(aquariumId: string | undefined): string {
  if (!aquariumId) throw new Error('aquariumId is required');
  return aquariumId;
}

export function useControllerSources(aquariumId: string | undefined) {
  return useQuery({
    queryKey: controllerKeys.sources(aquariumId),
    queryFn: () => listControllerSources(requireAquarium(aquariumId)),
    enabled: Boolean(aquariumId),
  });
}

export function useSensorMappings(aquariumId: string | undefined) {
  return useQuery({
    queryKey: controllerKeys.mappings(aquariumId),
    queryFn: () => listSensorMappings(requireAquarium(aquariumId)),
    enabled: Boolean(aquariumId),
  });
}

export function useSensorReadings(aquariumId: string | undefined, mappingId: string | null, days: number) {
  return useQuery({
    queryKey: controllerKeys.readings(aquariumId, mappingId ?? '', days),
    queryFn: () => listSensorReadings(requireAquarium(aquariumId), mappingId ?? '', days),
    enabled: Boolean(aquariumId) && Boolean(mappingId),
  });
}

export function useEnergySummary(aquariumId: string | undefined, year: number) {
  return useQuery({
    queryKey: controllerKeys.energy(aquariumId, year),
    queryFn: () => getEnergySummary(requireAquarium(aquariumId), year),
    enabled: Boolean(aquariumId),
  });
}

/** Source changes affect mappings and energy too (deleting a source deletes its data). */
function useInvalidateController(aquariumId: string | undefined) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: controllerKeys.all(aquariumId) });
}

export function useCreateControllerSource(aquariumId: string | undefined) {
  const invalidate = useInvalidateController(aquariumId);
  return useMutation({
    mutationFn: (name: string) => createControllerSource(requireAquarium(aquariumId), name),
    onSuccess: invalidate,
  });
}

export function useRevokeControllerSource(aquariumId: string | undefined) {
  const invalidate = useInvalidateController(aquariumId);
  return useMutation({
    mutationFn: (id: string) => revokeControllerSource(requireAquarium(aquariumId), id),
    onSuccess: invalidate,
  });
}

export function useDeleteControllerSource(aquariumId: string | undefined) {
  const invalidate = useInvalidateController(aquariumId);
  return useMutation({
    mutationFn: (id: string) => deleteControllerSource(requireAquarium(aquariumId), id),
    onSuccess: invalidate,
  });
}

export function useUpdateSensorMapping(aquariumId: string | undefined) {
  const invalidate = useInvalidateController(aquariumId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSensorMappingInput }) =>
      updateSensorMapping(requireAquarium(aquariumId), id, input),
    onSuccess: invalidate,
  });
}

export function useSetEnergyPrice(aquariumId: string | undefined) {
  const invalidate = useInvalidateController(aquariumId);
  return useMutation({
    mutationFn: (pricePerKwhCents: number | null) => setEnergyPrice(requireAquarium(aquariumId), pricePerKwhCents),
    onSuccess: invalidate,
  });
}
